import styled from '@emotion/styled'
import { Image } from 'app/components/Common/Image'
import { LogoMenu } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles } from 'app/theme'
import { motion } from 'framer-motion'
import React, { memo, useContext, useState } from 'react'

import { Contacts } from './Contacts'
import { MenuItem, Props as MenuItemProps } from './MenuItem'

export interface Props {
  menuItems: MenuItemProps[]
  modalStatus?: boolean
  onClick?: any
  pageID?: string
}

export const Navigation = memo(function Navigation({
  menuItems,
  modalStatus,
  pageID,
}: Props) {
  if (!menuItems) {
    return null
  }

  const [hoverImage, setHoverImage] = useState('')

  const onHoverLink = (title: string) => {
    setHoverImage(title)
  }

  const onHoverOutLink = () => {
    setHoverImage('')
  }

  let menuImages: any = []

  menuItems.map((item) => {
    if (item.childs) {
      item.childs.map((child) => {
        let image: any = {
          title: '',
          image: null,
        }

        image.alt = child.title
        image.id = child.id
        image.title = child.title
        image.image = child.menuImage

        menuImages.push(image)
      })
    } else {
      let image: any = {
        title: '',
        image: null,
      }

      image.alt = item.title
      image.id = item.id
      image.title = item.title
      image.image = item.menuImage

      menuImages.push(image)
    }
  })

  return (
    <Container
      className={modalStatus ? 'open' : undefined}
      onClick={(e) => e.stopPropagation()}
      data-lenis-prevent
      dial={4}
    >
      <LogoMenu />
      <Wrapper>
        <LeftSide space="between">
          <Nav>
            <NavList>
              {menuItems.slice(0, 3).map((item, index) => (
                <NavLi key={index}>
                  <MenuItem
                    {...item}
                    modalStatus={modalStatus}
                    onHoverLink={onHoverLink}
                    onHoverLinkOut={onHoverOutLink}
                    style={
                      modalStatus
                        ? {
                            transition: `opacity 0.4s ${0.1 * index + 0.6}s, transform 0.4s ${0.1 * index + 0.6}s, padding 0.4s`,
                            transform: 'none',
                          }
                        : {
                            transition: `opacity 0.4s ${0.05 * index}s, transform 0.4s ${0.05 * index}s, padding 0.4s`,
                            transform: 'translateY(100%)',
                          }
                    }
                  />
                </NavLi>
              ))}
            </NavList>
            <NavList>
              {menuItems.slice(3, 6).map((item, index) => (
                <NavLi key={index}>
                  <MenuItem
                    {...item}
                    modalStatus={modalStatus}
                    onHoverLink={onHoverLink}
                    onHoverLinkOut={onHoverOutLink}
                    style={
                      modalStatus
                        ? {
                            transition: `opacity 0.4s ${0.1 * (index + 3) + 0.6}s, transform 0.4s ${0.1 * (index + 3) + 0.6}s, padding 0.4s`,
                            transform: 'none',
                          }
                        : {
                            transition: `opacity 0.4s ${0.05 * (index + 3)}s, transform 0.4s ${0.05 * (index + 3)}s, padding 0.4s`,
                            transform: 'translateY(100%)',
                          }
                    }
                  />
                </NavLi>
              ))}
            </NavList>
            <NavList>
              {menuItems.slice(6).map((item, index) => (
                <NavLi key={index}>
                  <MenuItem
                    {...item}
                    modalStatus={modalStatus}
                    onHoverLink={onHoverLink}
                    onHoverLinkOut={onHoverOutLink}
                    style={
                      modalStatus
                        ? {
                            transition: `opacity 0.4s ${0.1 * (index + 6) + 0.6}s, transform 0.4s ${0.1 * (index + 6) + 0.6}s, padding 0.4s`,
                            transform: 'none',
                          }
                        : {
                            transition: `opacity 0.4s ${0.05 * (index + 6)}s, transform 0.4s ${0.05 * (index + 6)}s, padding 0.4s`,
                            transform: 'translateY(100%)',
                          }
                    }
                  />
                </NavLi>
              ))}
            </NavList>
          </Nav>

          <Contacts modalStatus={modalStatus} />
        </LeftSide>

        <Images className={modalStatus ? 'open' : undefined}>
          {menuImages.map((item: any, index: any) => {
            if (!item.image) return

            return (
              <Img
                alt={item.alt}
                className={
                  pageID === item.id && !hoverImage
                    ? 'default'
                    : hoverImage === item.title
                      ? 'active'
                      : undefined
                }
                key={index}
                {...item.image}
              />
            )
          })}
        </Images>
      </Wrapper>
    </Container>
  )
})

const Container = styled(FlexBox)`
  width: 100%;
  height: 100%;
  background: url('/illustration-background-light.jpg');
  background-repeat: no-repeat;
  background-attachment: fixed;
  background-size: cover;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  transition: 2s 2s ease-in-out;

  &:before {
    content: '';
    position: absolute;
    top: 5rem;
    left: 100%;
    right: 0;
    width: 0;
    height: 0.0625rem;
    background: ${({ theme }) => theme.colors.variants.neutralDark2};
    opacity: 0.15;
    transition: 0.4s ease-in-out;
  }

  &.open {
    &:before {
      left: 0;
      width: 100%;
      transition: 1.5s 0.5s ease-in-out;
    }
  }

  > svg {
    position: absolute;
    top: 1.375rem;
    left: 50%;
    transform: translateX(-50%);
    width: 8.0625rem;
    height: 2.25rem;
    fill: ${({ theme }) => theme.colors.variants.neutralDark2};
  }

  opacity: 0;
  transform: translateY(-100%);

  > div:last-of-type {
    opacity: 0;
    transition: 0.6s ease-in-out;
  }

  &.open {
    transition: 0.6s ease-in-out;
    opacity: 1;
    transform: none;

    > div:last-of-type {
      opacity: 1;
      transition: 0.6s 0.6s ease-in-out;
    }
  }

  @media (max-width: 1199px) {
    > svg {
      top: 1.125rem;
      width: 8rem;
      height: 2.25rem;
    }

    &:before {
      top: 4.5rem;
    }
  }

  @media (max-width: 991px) {
    > svg {
      display: none;
    }
  }

  @media (max-width: 439px) {
    background: url('/illustration-background-light-mobile.jpg');
  }
`

const Wrapper = styled(FlexBox)`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  padding-top: 9.5rem;
  -webkit-overflow-scrolling: auto;

  @media (max-width: 1199px) {
    display: block;
    padding-top: 0.625rem;
  }
`

const LeftSide = styled(FlexBox)`
  width: 55.972vw;
  height: 100%;
  position: relative;

  @media (max-width: 1199px) {
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    transform: none;
  }

  .item-contacts {
    position: absolute;
    bottom: 3.75rem;
    right: 0;
    left: 5.833vw;

    @media (max-width: 1439px) {
      display: none;
    }
  }
`

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  position: absolute;
  top: -3rem;
  right: 0;
  left: 5.833vw;

  @media (max-width: 1199px) {
    flex-direction: column;
    top: 4.5rem;
    left: 0;
    padding-left: 2.25rem;
    padding-top: 1.375rem;
  }
  @media (max-width: 767px) {
    padding-bottom: 1.875rem;
    top: 4.375rem;
  }
`

const NavList = styled.ul`
  min-width: 12.5rem;

  @media (max-width: 1199px) {
    width: 100%;
  }
`

const NavLi = styled(motion.li)`
  margin-top: 3rem;

  .item-book-cta {
    margin-top: 0.625rem;
    display: block;
    transition: 0.2s cubic-bezier(0.25, 1, 0.5, 1);

    &:hover {
      transition: 0.2s cubic-bezier(0.25, 1, 0.5, 1);
      opacity: 0.6;
    }
  }

  @media (max-width: 1199px) {
    margin-top: 1.5rem;

    .item-book-cta {
      display: none;
    }
  }
`

const Images = styled.div`
  width: 30.556vw;
  aspect-ratio: 0.75;
  position: fixed;
  top: 9.5rem;
  right: 6.667vw;
  filter: blur(1.25rem);
  opacity: 0;
  transition:
    opacity 0.5s 1s,
    transform 0.5s 1s,
    filter 0.5s 1s;
  z-index: 2;
  &.open {
    filter: blur(0);
    opacity: 1;
  }

  @media (max-width: 1439px) {
    bottom: 6.667vw;
    height: auto;
    aspect-ratio: unset;
  }

  @media (max-width: 1199px) {
    display: none;
  }
`

const Img = styled(Image)`
  img {
    ${styles.img}

    filter: blur(1.25rem);
    opacity: 0 !important;
    transition:
      opacity 0.5s,
      transform 0.5s,
      filter 0.5s;
  }

  &.active,
  &.default {
    img {
      filter: blur(0);
      opacity: 1 !important;
    }
  }
`
